import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BillingEvent {
  id: string;
  tenant_id: string;
  event_type: string;
  amount: number;
  recorded_at: string;
}

export interface MrrDataPoint {
  date: string;
  mrr: number;
}

/**
 * Fetch raw billing events for all tenants within a date range.
 */
export function useBillingEvents(days: number = 90) {
  return useQuery({
    queryKey: ["tenant-billing-events", days],
    queryFn: async () => {
      const since = new Date();
      since.setDate(since.getDate() - days);
      const { data, error } = await supabase
        .from("tenant_billing_events" as any)
        .select("*")
        .gte("recorded_at", since.toISOString())
        .order("recorded_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as BillingEvent[];
    },
    staleTime: 60_000,
  });
}

/**
 * Compute MRR trend from billing events.
 * Returns daily MRR data points for charting.
 */
export function useMrrTrend(days: number = 30) {
  const { data: events = [], isLoading } = useBillingEvents(90);

  const trendData: MrrDataPoint[] = [];
  let netNewMrr = 0;
  let churnedMrr = 0;
  let expansionMrr = 0;

  if (events.length > 0) {
    // Sort events by date
    const sorted = [...events].sort(
      (a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime()
    );

    // Build cumulative MRR day by day
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    const today = new Date();

    // Pre-calculate initial MRR from events before the window
    let runningMrr = 0;
    for (const ev of sorted) {
      if (new Date(ev.recorded_at) < startDate) {
        runningMrr += Number(ev.amount);
      }
    }

    // Events within window
    const windowEvents = sorted.filter(
      (e) => new Date(e.recorded_at) >= startDate
    );

    // Calculate summary stats
    for (const ev of windowEvents) {
      const amt = Number(ev.amount);
      switch (ev.event_type) {
        case "new":
          netNewMrr += amt;
          break;
        case "expansion":
          expansionMrr += amt;
          break;
        case "churn":
          churnedMrr += Math.abs(amt);
          break;
        case "contraction":
          churnedMrr += Math.abs(amt);
          break;
      }
    }

    // Generate daily data points
    let eventIdx = 0;
    for (let d = new Date(startDate); d <= today; d.setDate(d.getDate() + 1)) {
      const dayStr = d.toISOString().split("T")[0];
      // Apply events for this day
      while (
        eventIdx < windowEvents.length &&
        new Date(windowEvents[eventIdx].recorded_at).toISOString().split("T")[0] <= dayStr
      ) {
        runningMrr += Number(windowEvents[eventIdx].amount);
        eventIdx++;
      }
      trendData.push({ date: dayStr, mrr: Math.max(0, runningMrr) });
    }
  }

  return {
    trendData,
    netNewMrr,
    churnedMrr,
    expansionMrr,
    currentMrr: trendData.length > 0 ? trendData[trendData.length - 1].mrr : 0,
    isLoading,
  };
}

/**
 * Fetch onboarding steps for a specific tenant.
 */
export function useTenantOnboarding(tenantId: string | null) {
  return useQuery({
    queryKey: ["tenant-onboarding", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenant_onboarding_steps" as any)
        .select("*")
        .eq("tenant_id", tenantId!);
      if (error) throw error;
      return (data ?? []) as unknown as Array<{
        id: string;
        tenant_id: string;
        step_key: string;
        completed: boolean;
        completed_at: string | null;
        completed_by: string | null;
        note: string | null;
      }>;
    },
    enabled: !!tenantId,
    staleTime: 60_000,
  });
}
